import { Form, Formik } from 'formik';
import React, { Dispatch, SetStateAction, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

import { PrimaryButton, TextButton } from '@components/Button/variants';
import TextField from '@components/Form/TextField';
import useCreateSession from '@hooks/useCreateSession';
import { new_password_reset_path } from '@routes';
import { READ_ONLY_MODE } from '@src/env';

import { FormRenderMap } from './NewSessionPage';

const validationSchema = Yup.object({
  password: Yup.string().required('You must enter a password'),
});

interface PasswordFormParams {
  email: string;
  returningUrl: string | null;
  setEmail: Dispatch<SetStateAction<string>>;
  setCurrentForm: Dispatch<SetStateAction<string>>;
}

const PasswordForm: React.FC<PasswordFormParams> = ({
  email,
  returningUrl,
  setEmail,
  setCurrentForm,
}) => {
  const [searchParams] = useSearchParams();
  const queryEmail = searchParams.get('email');
  const {
    mutateAsync: createSessionMutateAsync,
    isLoading: createSessionIsLoading,
    data: createSessionResponse,
  } = useCreateSession();
  const initialValues = {};

  useEffect(() => {
    if (queryEmail) {
      setEmail(queryEmail);
    }
  }, [queryEmail, setEmail]);

  useEffect(() => {
    if (!createSessionIsLoading && createSessionResponse) {
      if (!createSessionResponse.success) {
        const errorMsg = createSessionResponse.error.message || 'Invalid password';
        toast.error(errorMsg);
      }
    }
  }, [createSessionIsLoading, createSessionResponse]);

  const handleSubmit = (values) => {
    createSessionMutateAsync({
      email: email,
      password: values.password,
      returningUrl: returningUrl,
    });
  };

  return (
    <>
      <div className="mb-12 flex flex-col justify-center text-center font-suisse-works">
        <div className="text-[15px]">Welcome back {email}</div>
        <div className="text-[15px]">
          Not your account?{' '}
          <TextButton
            as="link"
            className="inline-flex font-suisse-neue text-[15px] text-primary-magenta no-underline"
            onClick={() => setCurrentForm(FormRenderMap.email)}
          >
            Use a different email
          </TextButton>
        </div>
      </div>
      {!READ_ONLY_MODE && (
        <>
          <div className="mb1">Check your inbox for your magic 1-click login link</div>
          <div className="middle-hr mt1 mb1">OR</div>
        </>
      )}

      <div className="mt1 w-full">
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
          validateOnMount={true}
        >
          {(formik) => (
            <Form id="new_registration">
              <TextField
                inputClassName=""
                name="password"
                placeholder="Password"
                required={true}
                type="password"
              />
              <PrimaryButton
                as="button"
                className={'mt-8 w-full'}
                isLoading={createSessionIsLoading}
                disabled={!formik.isValid}
              >
                Sign In
              </PrimaryButton>
            </Form>
          )}
        </Formik>
        {!READ_ONLY_MODE && (
          <div>
            <a href={new_password_reset_path()} className="text-[15px]" id="password-reset-link">
              Forgot Password?
            </a>
          </div>
        )}
      </div>
    </>
  );
};

export default PasswordForm;
